import { useDialog } from '@repo/ui';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { GraphQLTaggedNode, useMutation } from 'react-relay';

import {
  businessAnswerUpsertMutation,
  businessAnswerUpsertMutation$data,
  businessAnswerUpsertMutation$variables
} from '@/__generated__/businessAnswerUpsertMutation.graphql';
import { useSurveyErrorDialogAttributes } from '@/components/survey/common/hooks/use-survey-error-dialog-attributes';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { isCompletedCurrentBusinessSurveyAtom, locationsDataAtom } from '@/lib/stores/survey/business';
import { currentBusinessBmanTinAtom, surveyAnswerIdAtom } from '@/lib/stores/survey/common';
import { LocationType } from '@/lib/types/survey';

type UpsertBusinessAnswerFunction = (
  variables: Partial<Omit<businessAnswerUpsertMutation$variables, 'surveyAnswerId' | 'bmanTin'>>,
  callbacks: {
    onCompleted: (res: businessAnswerUpsertMutation$data['upsertBusinessAnswer']) => void;
    onError?: () => void;
  }
) => void;

export const useUpsertBusinessAnswer = (mutationQuery: GraphQLTaggedNode): [UpsertBusinessAnswerFunction, boolean] => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const [upsertAnswer, isLoading] = useMutation<businessAnswerUpsertMutation>(mutationQuery);
  const surveyAnswerId = useAtomValue(surveyAnswerIdAtom);
  const bmanTin = useAtomValue(currentBusinessBmanTinAtom);
  const isCompleted = useAtomValue(isCompletedCurrentBusinessSurveyAtom);
  const setLocationsData = useSetAtom(locationsDataAtom);
  const { alreadyCompleted, invalidSurveyData } = useSurveyErrorDialogAttributes();

  const showErrorDialog = () => {
    showDialog({
      title: '일시적인 오류가 발생했어요.\n잠시 후 다시 시도해주세요.'
    });
  };

  const upsertBusinessAnswer: UpsertBusinessAnswerFunction = (variables, { onCompleted, onError }) => {
    upsertAnswer({
      variables: Object.assign({}, variables, {
        surveyAnswerId,
        bmanTin,
        isCompleted: variables.isCompleted || isCompleted
      }),
      onCompleted: (response, errors) => {
        if (!response.upsertBusinessAnswer.result || !!response.upsertBusinessAnswer.errors || !!errors) {
          const surveyError = response.upsertBusinessAnswer.errors;
          switch (surveyError?.code) {
            case 'ERR_SURVEY_EXPIRED': {
              router.push(SURVEY_PAGES.만료);
              return;
            }
            case 'ERR_SURVEY_COMPLETED': {
              showDialog(alreadyCompleted);
              return;
            }
            case 'ERR_UPDATE_SURVEY_ANSWER': {
              showDialog(invalidSurveyData);
              return;
            }
          }
          showErrorDialog();
          return;
        }

        if (variables.isFirstLocation || variables.locations) {
          setLocationsData((prev) => ({
            ...prev,
            ...(variables.isFirstLocation && { isFirstLocation: variables.isFirstLocation }),
            ...(variables.locations && {
              locations: variables.locations.map(
                ({ address, year, isUnknownRelocationYear, isFirstLocation }): LocationType => ({
                  address,
                  isUnknownRelocationYear,
                  isFirstLocation,
                  year: year || 0,
                  isHometaxRegAddress: address === prev.hometaxAddress
                })
              )
            })
          }));
        }

        onCompleted(response.upsertBusinessAnswer);
      },
      onError: () => {
        if (onError) {
          onError();
          return;
        }
        showErrorDialog();
      }
    });
  };

  return [upsertBusinessAnswer, isLoading];
};
